import { useContext, useEffect, useState, useMemo } from "react";
import { Button, Tabs, Tag } from "antd";
import { useHistory, useLocation } from "react-router-dom";
import { client } from "services/axios";
import { BASE_URL } from "config/config";
import { getAccessToken } from "services/auth";
import { embedDashboard } from "@superset-ui/embedded-sdk";
import {
  RoleSubscriptionEnum,
  SubscriptionContext
} from "context/SubscriptionProvider";
import { supersetEnv } from "utils/utils";

const waitForContainer = (
  containerId: string,
  maxAttempts = 10
): Promise<HTMLElement> => {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const checkContainer = () => {
      const container = document.getElementById(containerId);
      if (container) {
        resolve(container);
      } else if (attempts < maxAttempts) {
        attempts += 1;
        setTimeout(checkContainer, 100);
      } else {
        reject(new Error("Dashboard container not found after waiting"));
      }
    };

    checkContainer();
  });
};

export default function SubscriptionReports() {
  const history = useHistory();
  const location = useLocation();
  const subscriptionToken: any = getAccessToken();

  const {
    subscriptionRole,
    isFeatureFlagEnabled: isFeatureFlagSubscriptionEnabled
  } = useContext(SubscriptionContext);

  useEffect(() => {
    if (
      subscriptionRole < RoleSubscriptionEnum.subscription_admin &&
      location.pathname.endsWith("/analytics")
    ) {
      history.replace("/projects");
    }
  }, [location.pathname, history, subscriptionRole]);

  const isExecutiveOverviewDashboardEnabled = isFeatureFlagSubscriptionEnabled(
    "EXECUTIVE_OVERVIEW_DASHBOARD"
  );
  const isUsageDashboardEnabled = isFeatureFlagSubscriptionEnabled(
    "USAGE_SUMMARY_DASHBOARD"
  );

  const availableTabs = useMemo(() => {
    const tabs: string[] = [];
    if (isExecutiveOverviewDashboardEnabled) {
      tabs.push("1");
    }
    if (isUsageDashboardEnabled) {
      tabs.push("2");
    }
    return tabs;
  }, [isExecutiveOverviewDashboardEnabled, isUsageDashboardEnabled]);

  const defaultTab = availableTabs[0] || "1";

  const [tabIndex, setTabIndex] = useState<string>(defaultTab);

  useEffect(() => {
    if (!availableTabs.includes(tabIndex)) {
      setTabIndex(defaultTab);
    }
  }, [availableTabs, tabIndex, defaultTab]);

  const handleExport = () => {
    const dashboardNo = tabIndex === "1" ? 10 : 8;
    window.open(`/analytics/export?dashboard_no=${dashboardNo}`, "_blank");
  };

  useEffect(() => {
    if (tabIndex === "1" && isExecutiveOverviewDashboardEnabled) {
      const container = document.getElementById("usage-dashboard-container");
      const accountSummaryData = async () => {
        try {
          if (!container) {
            console.error("Dashboard container not found.");
            return;
          }

          const tokens = await client.post(
            `${BASE_URL}/superset/dashboard`,
            {
              dashboard_no: 5
            },
            {
              headers: {
                Authorization: `Bearer ${subscriptionToken}`,
                "Content-Type": "application/json"
              }
            }
          );

          embedDashboard({
            id: tokens.data.dashboard_id,
            supersetDomain: supersetEnv(),
            mountPoint: container,
            fetchGuestToken: () => Promise.resolve(tokens.data.token),
            dashboardUiConfig: {
              hideTitle: true,
              hideChartControls: false,
              hideTab: true
              // filters: {
              //   visible: false,
              //   expanded: false,
              //   allowEdit: false
              // }
            }
          });
        } catch (error) {
          console.error("Error posting data:", error);
          throw error;
        }
      };

      if (!container?.hasChildNodes()) {
        accountSummaryData();
      }
    }
  }, [subscriptionToken, tabIndex, isExecutiveOverviewDashboardEnabled]);

  useEffect(() => {
    if (tabIndex === "2" && isUsageDashboardEnabled) {
      const projectUsageData = async () => {
        try {
          const container = await waitForContainer(
            "executive-dashboard-container"
          );
          // waitForContainer only ever resolves with a truthy HTMLElement (resolve(container) is called solely inside `if (container)`), so this guard is unreachable
          /* v8 ignore next 4 */
          if (!container) {
            console.error("Dashboard container not found.");
            return;
          }

          const tokens = await client.post(
            `${BASE_URL}/superset/dashboard`,
            {
              dashboard_no: 2
            },
            {
              headers: {
                Authorization: `Bearer ${subscriptionToken}`,
                "Content-Type": "application/json"
              }
            }
          );

          embedDashboard({
            id: tokens.data.dashboard_id,
            supersetDomain: supersetEnv(),
            mountPoint: container,
            fetchGuestToken: () => Promise.resolve(tokens.data.token),
            dashboardUiConfig: {
              hideTitle: true,
              hideChartControls: false,
              hideTab: true
              // filters: {
              //   visible: false,
              //   expanded: false,
              //   allowEdit: false
              // }
            }
          });
        } catch (error) {
          console.error("Error posting data:", error);
          throw error;
        }
      };

      const container = document.getElementById(
        "subscription-dashboard-container2"
      );
      if (!container?.hasChildNodes()) {
        projectUsageData();
      }
    }
  }, [subscriptionToken, tabIndex, isUsageDashboardEnabled]);

  const tabItems = [];

  if (isExecutiveOverviewDashboardEnabled) {
    tabItems.push({
      key: "1",
      label: "Executive Overview",
      children: (
        <div
          id="usage-dashboard-container"
          className="w-full h-full hideScroll !h-[80vh]"
        />
      )
    });
  }

  if (isUsageDashboardEnabled) {
    tabItems.push({
      key: "2",
      label: "Usage Summary",
      children: (
        <div
          id="executive-dashboard-container"
          className="w-full h-full hideScroll !h-[80vh]"
        />
      )
    });
  }

  return (
    <div className="flex flex-col h-full w-full">
      <div className="px-8 py-4 flex justify-between items-center">
        <div className="flex space-x-2 items-center text-black">
          <span className="text-xl font-semibold">Analytics </span>
          <span className="pl-[5px]">
            <Tag>Beta</Tag>
          </span>
        </div>
        <Button type="primary" onClick={handleExport}>
          Export PDF/Print
        </Button>
      </div>
      <div className="w-full pl-[19px]">
        {tabItems.length > 0 ? (
          <Tabs
            data-testid="index-tabs-subs-reports"
            tabPlacement="top"
            onChange={(activeKey: any) => {
              setTabIndex(activeKey);
            }}
            activeKey={tabIndex}
            items={tabItems}
          />
        ) : (
          <div className="px-8 py-4 text-gray-500">
            No dashboards available. Please check your feature flags.
          </div>
        )}
      </div>
    </div>
  );
}
